import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Typography,
  IconButton,
  Button,
  InputAdornment,
  Alert,
} from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { referenceService } from "src/api/references/referenceService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import {
  requestService,
  WillOpenCardKey,
} from "src/api/request/requestService";
import { axiosInstance } from "src/api/config/axiosInstance";
import { useTranslate } from "src/locales";

interface AskDoctorModalProps {
  open: boolean;
  onClose: () => void;
  serviceName?: string;
  onSave?: (data: {
    arrivalType: string;
    departmentName: string;
    doctorName: string;
    doctorRole: string;
    referenceDoctor: string;
    isReferenceDoctor: boolean;
    assistantDoctor: string;
    referringPersonnel: string;
  }) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const AskDoctorModal: React.FC<AskDoctorModalProps> = ({
  open,
  onClose,
  serviceName = "Seçilen Hizmet Adı",
  onSave,
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));

  const [arrivalType, setArrivalType] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [doctorRole, setDoctorRole] = useState("");
  const [referenceDoctor, setReferenceDoctor] = useState("");
  const [referenceDoctorRole, setReferenceDoctorRole] = useState("");
  const [isReferenceDoctor, setIsReferenceDoctor] = useState(false);
  const [assistantDoctor, setAssistantDoctor] = useState("");
  const [referringPersonnel, setReferringPersonnel] = useState("");
  const [error, setError] = useState("");
  const [arrivalOptions, setArrivalOptions] = useState<{ label: string; value: string }[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>(
    []
  );
  const [doctorOptions, setDoctorOptions] = useState<{ label: string; value: string }[]>([]);
  const [doctorRoleOptions, setDoctorRoleOptions] = useState<{ label: string; value: string }[]>([]);
  const [referenceDoctorOptions, setReferenceDoctorOptions] = useState<{ label: string; value: string }[]>([]);
  const [assistantDoctorOptions, setAssistantDoctorOptions] = useState<{ label: string; value: string }[]>([]);
  const [referringPersonnelOptions, setReferringPersonnelOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingArrival, setLoadingArrival] = useState(false);
  const [loadingDept, setLoadingDept] = useState(false);
  const [loadingDoctor, setLoadingDoctor] = useState(false);
  const [loadingDoctorRole, setLoadingDoctorRole] = useState(false);
  const [loadingReferenceDoctor, setLoadingReferenceDoctor] = useState(false);
  const [loadingAssistantDoctor, setLoadingAssistantDoctor] = useState(false);
  const [loadingReferringPersonnel, setLoadingReferringPersonnel] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);

  const resetAfterArrivalChange = () => {
    setDepartmentName("");
    setDoctorName("");
    setDoctorRole("");
    setReferenceDoctor("");
    setReferenceDoctorRole("");
    setIsReferenceDoctor(false);
    setAssistantDoctor("");
    setReferringPersonnel("");
    setDepartmentOptions([]);
    setDoctorOptions([]);
    setDoctorRoleOptions([]);
    setReferenceDoctorOptions([]);
    setAssistantDoctorOptions([]);
  };

  const resetAfterDepartmentChange = () => {
    setDoctorName("");
    setDoctorRole("");
    setReferenceDoctor("");
    setIsReferenceDoctor(false);
    setAssistantDoctor("");
    setDoctorOptions([]);
    setDoctorRoleOptions([]);
    setReferenceDoctorOptions([]);
    setAssistantDoctorOptions([]);
  };

  const resetAfterDoctorChange = () => {
    setDoctorRole("");
    setDoctorRoleOptions([]);
  };

  // Seçili değerlerin label'larını hesapla
  const selectedArrivalTypeLabel = useMemo(() => {
    const found = arrivalOptions.find((opt) => opt.value === arrivalType);
    return found?.label || "";
  }, [arrivalType, arrivalOptions]);

  const selectedDepartmentLabel = useMemo(() => {
    const found = departmentOptions.find((opt) => opt.value === departmentName);
    return found?.label || "";
  }, [departmentName, departmentOptions]);

  const selectedDoctorLabel = useMemo(() => {
    const found = doctorOptions.find((opt) => opt.value === doctorName);
    return found?.label || "";
  }, [doctorName, doctorOptions]);

  const selectedDoctorRoleLabel = useMemo(() => {
    const found = doctorRoleOptions.find((opt) => opt.value === doctorRole);
    return found?.label || "";
  }, [doctorRole, doctorRoleOptions]);

  const selectedReferenceDoctorLabel = useMemo(() => {
    const found = referenceDoctorOptions.find((opt) => opt.value === referenceDoctor);
    return found?.label || "";
  }, [referenceDoctor, referenceDoctorOptions]);

  const selectedAssistantDoctorLabel = useMemo(() => {
    const found = assistantDoctorOptions.find((opt) => opt.value === assistantDoctor);
    return found?.label || "";
  }, [assistantDoctor, assistantDoctorOptions]);

  const selectedReferringPersonnelLabel = useMemo(() => {
    const found = referringPersonnelOptions.find((opt) => opt.value === referringPersonnel);
    return found?.label || "";
  }, [referringPersonnel, referringPersonnelOptions]);

  const requiredFilled = useMemo(() => {
    const base =
      arrivalType &&
      departmentName &&
      doctorName &&
      doctorRole;
    const refOk = !isReferenceDoctor || (!!referenceDoctor && referenceDoctor.trim() !== "");
    return base && refOk;
  }, [arrivalType, departmentName, doctorName, doctorRole, isReferenceDoctor, referenceDoctor]);

  useEffect(() => {
    if (open) {
      setArrivalType("");
      setDepartmentName("");
      setDoctorName("");
      setDoctorRole("");
      setReferenceDoctor("");
      setReferenceDoctorRole("");
      setIsReferenceDoctor(false);
      setAssistantDoctor("");
      setReferringPersonnel("");
      setError("");
      setDoctorOptions([]);
      setDoctorRoleOptions([]);
      setReferenceDoctorOptions([]);
      setAssistantDoctorOptions([]);
      setReferringPersonnelOptions([]);

      // load arrival types
      const fetchArrival = async () => {
        try {
          setLoadingArrival(true);
          // Geliş Tipi: reference/api/AskDoctor/patient-coming-types
          const res = await axiosInstance.get("reference/api/AskDoctor/patient-coming-types");
          const list = (res as any)?.data?.data ?? (res as any)?.data ?? [];
          if (Array.isArray(list)) {
            setArrivalOptions(
              list.map((it: any) => ({
                label: it.name ?? "",
                value: String(it.id),
              }))
            );
          } else {
            setArrivalOptions([]);
          }
        } catch (err) {
          console.error("arrival types load error", err);
          setArrivalOptions([]);
        } finally {
          setLoadingArrival(false);
        }
      };

      fetchArrival();
      // Bölüm Adı fetch'i Geliş Tipi seçilince tetiklenecek (aşağıdaki useEffect)
    }
  }, [open, selectedOfficeId, additionalInfoParams]);

  // Geliş Tipi seçilince Bölüm Adı fetch'i tetikle
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!arrivalType || !selectedOfficeId) {
        setDepartmentOptions([]);
        setDepartmentName(""); // Bölüm seçimini sıfırla
        setDoctorName(""); // Doktor seçimini de sıfırla
        setDoctorRole(""); // Doktor rolünü de sıfırla
        setAssistantDoctor(""); // Asistan doktor seçimini de sıfırla
        setDoctorOptions([]); // Doktor listesini temizle
        setDoctorRoleOptions([]); // Doktor rol listesini temizle
        setAssistantDoctorOptions([]); // Asistan doktor listesini temizle
        return;
      }
      try {
        setLoadingDept(true);
        // Bölüm Adı: reference/api/AskDoctor/departments
        // API: DT_ID, OFF_ID ve opsiyonel Service ID'ye göre departmanları getirir
        const res = await axiosInstance.post("reference/api/AskDoctor/departments", {
          dtid: Number(arrivalType), // Department Type ID (patient-coming-types'tan dönen id)
          offid: Number(selectedOfficeId),
          serviceid: additionalInfoParams?.serviceId ?? 0, // Opsiyonel: Branch Code filtresi için
        });
        // Response yapısı: { data: [...], issuccess: true, statuscode: 200, message: "" }
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setDepartmentOptions(
            list
              .filter((d: any) => d.isactive === "1" || d.isactive === 1) // Sadece aktif departmanlar
              .map((d: any) => ({
                label: d.name ?? "",
                value: String(d.id),
              }))
          );
        } else {
          setDepartmentOptions([]);
        }
      } catch (err) {
        console.error("departments load error", err);
        setDepartmentOptions([]);
      } finally {
        setLoadingDept(false);
      }
    };

    fetchDepartments();
  }, [arrivalType, selectedOfficeId, additionalInfoParams]);

  // fetch doctors when department changes
  useEffect(() => {
    const fetchDoctors = async () => {
      if (!departmentName || !selectedOfficeId) {
        setDoctorOptions([]);
        setDoctorName(""); // Doktor seçimini sıfırla
        setDoctorRole(""); // Doktor rolünü de sıfırla
        setAssistantDoctor(""); // Asistan doktor seçimini de sıfırla
        setDoctorRoleOptions([]); // Doktor rol listesini temizle
        setAssistantDoctorOptions([]); // Asistan doktor listesini temizle
        return;
      }
      try {
        setLoadingDoctor(true);
        // Müsait doktorları getir: reference/api/AskDoctor/available-doctors
        const res = await axiosInstance.post("reference/api/AskDoctor/available-doctors", {
          deptid: Number(departmentName), // Department ID
          offid: Number(selectedOfficeId), // Office ID
          date: new Date().toISOString(), // Opsiyonel: Tarih (varsayılan: şu anki zaman)
        });
        // Response yapısı: { data: [...], issuccess: true, statuscode: 200, message: "" }
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setDoctorOptions(
            list.map((d: any) => ({
              label: `${d.drtitle || ""} ${d.name || ""} ${d.surname || ""}`.trim(),
              value: String(d.id),
            }))
          );
        } else {
          setDoctorOptions([]);
        }
      } catch (err) {
        console.error("available doctors load error", err);
        setDoctorOptions([]);
      } finally {
        setLoadingDoctor(false);
      }
    };
    fetchDoctors();
  }, [departmentName, selectedOfficeId]);

  // Doktor seçilince doktor rolü fetch'i tetikle
  useEffect(() => {
    const fetchDoctorRoles = async () => {
      if (!doctorName || !departmentName) {
        setDoctorRoleOptions([]);
        setDoctorRole(""); // Doktor rolünü sıfırla
        return;
      }
      try {
        setLoadingDoctorRole(true);
        // Doktor rolü: reference/api/AskDoctor/roles
        const res = await axiosInstance.post("reference/api/AskDoctor/roles", {
          drid: Number(doctorName), // Doktor ID
          deptid: Number(departmentName), // Opsiyonel: Department ID (0 ise tüm departmanlar)
        });
        // Response yapısı: { data: [...], issuccess: true, statuscode: 200, message: "" }
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setDoctorRoleOptions(
            list.map((r: any) => ({
              label: r.name ?? "",
              value: String(r.id),
            }))
          );
        } else {
          setDoctorRoleOptions([]);
        }
      } catch (err) {
        console.error("doctor roles load error", err);
        setDoctorRoleOptions([]);
      } finally {
        setLoadingDoctorRole(false);
      }
    };
    fetchDoctorRoles();
  }, [doctorName, departmentName]);

  // Referans Doktor checkbox'ı işaretliyse referans doktorları getir
  useEffect(() => {
    const fetchReferenceDoctors = async () => {
      if (!isReferenceDoctor || !departmentName || !selectedOfficeId) {
        setReferenceDoctorOptions([]);
        setReferenceDoctor(""); // Referans doktor seçimini sıfırla
        return;
      }
      try {
        setLoadingReferenceDoctor(true);
        // Referans doktorları getir: reference/api/AskDoctor/reference-doctors
        const res = await axiosInstance.post("reference/api/AskDoctor/reference-doctors", {
          deptid: Number(departmentName), // Department ID
          offid: Number(selectedOfficeId), // Office ID
          date: new Date().toISOString(), // Opsiyonel: Tarih (varsayılan: şu anki zaman)
        });
        // Response yapısı: { data: [...], issuccess: true, statuscode: 200, message: "" }
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setReferenceDoctorOptions(
            list.map((d: any) => ({
              label: `${d.drtitle || ""} ${d.name || ""} ${d.surname || ""}`.trim(),
              value: String(d.id),
            }))
          );
        } else {
          setReferenceDoctorOptions([]);
        }
      } catch (err) {
        console.error("reference doctors load error", err);
        setReferenceDoctorOptions([]);
      } finally {
        setLoadingReferenceDoctor(false);
      }
    };
    fetchReferenceDoctors();
  }, [isReferenceDoctor, departmentName, selectedOfficeId]);

  // Bölüm seçilince asistan doktorları getir
  useEffect(() => {
    const fetchAssistantDoctors = async () => {
      if (!departmentName || !selectedOfficeId) {
        setAssistantDoctorOptions([]);
        setAssistantDoctor(""); // Asistan doktor seçimini sıfırla
        return;
      }
      try {
        setLoadingAssistantDoctor(true);
        // Asistan doktorları getir: reference/api/AskDoctor/assistant-doctors
        const res = await axiosInstance.post("reference/api/AskDoctor/assistant-doctors", {
          deptid: Number(departmentName), // Department ID
          offid: Number(selectedOfficeId), // Office ID
          date: new Date().toISOString(), // Opsiyonel: Tarih (varsayılan: şu anki zaman)
        });
        // Response yapısı: { data: [...], issuccess: true, statuscode: 200, message: "" }
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setAssistantDoctorOptions(
            list.map((d: any) => ({
              label: `${d.drtitle || ""} ${d.name || ""} ${d.surname || ""}`.trim(),
              value: String(d.id),
            }))
          );
        } else {
          setAssistantDoctorOptions([]);
        }
      } catch (err) {
        console.error("assistant doctors load error", err);
        setAssistantDoctorOptions([]);
      } finally {
        setLoadingAssistantDoctor(false);
      }
    };
    fetchAssistantDoctors();
  }, [departmentName, selectedOfficeId]);

  // Modal açılınca yönlendiren personelleri getir
  useEffect(() => {
    if (!open) return;

    const fetchReferringPersonnel = async () => {
      try {
        setLoadingReferringPersonnel(true);
        // Yönlendiren personelleri getir: reference/api/AskDoctor/directing-staff
        const res = await axiosInstance.post("reference/api/AskDoctor/directing-staff", {
          offid: selectedOfficeId ? String(selectedOfficeId) : undefined, // Opsiyonel: Şube ID listesi
          pertype: "1", // Opsiyonel: Personel Tipi Kodu (varsayılan: '1' - Doktor)
        });
        // Response yapısı: { data: [...], issuccess: true, statuscode: 200, message: "" }
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setReferringPersonnelOptions(
            list.map((p: any) => ({
              label: `${p.drtitle || ""} ${p.name || ""} ${p.surname || ""}`.trim(),
              value: String(p.id),
            }))
          );
        } else {
          setReferringPersonnelOptions([]);
        }
      } catch (err) {
        console.error("directing staff load error", err);
        setReferringPersonnelOptions([]);
      } finally {
        setLoadingReferringPersonnel(false);
      }
    };
    fetchReferringPersonnel();
  }, [open, selectedOfficeId]);

  const handleSave = () => {
    if (!requiredFilled) {
      setError("Geliş tipi, Bölüm, Doktor adı/rolü zorunlu. Referans doktor seçiliyse doldurun.");
      return;
    }
    setError("");
    onSave?.({
      arrivalType,
      departmentName,
      doctorName,
      doctorRole,
      referenceDoctor,
      isReferenceDoctor,
      assistantDoctor,
      referringPersonnel,
    });
    if (!onSave) {
      onClose();
    }
  };

  // WillOpenCard KEY bazlı ek bilgi formunu merkezi servisten yükle
  useEffect(() => {
    if (!open || !additionalInfoKey || !additionalInfoParams) return;

    const loadAdditionalInfo = async () => {
      try {
        const res = await requestService.getAdditionalInfoForKey({
          key: additionalInfoKey,
          serviceId: additionalInfoParams.serviceId,
          requestDetailId: additionalInfoParams.requestDetailId,
          ptId: additionalInfoParams.ptId,
          officeId: additionalInfoParams.officeId,
        });
        setAdditionalInfo(res.data);
        // TODO: Dönen ek bilgi ile formu pre-fill etmek için kullanılabilir.
      } catch {
        // Hata durumunda mevcut manuel akış devam eder.
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoKey, additionalInfoParams]);

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700} color="#111827">
            {t("patient-request.ask-doctor.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {serviceName}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 2,
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={!requiredFilled} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>
        
        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t("patient-request.ask-doctor.arrival-type")}
          </Typography>
        </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
              <ByzSelectListPopupInput
                fullWidth
                size="small"
              label={t("patient-request.ask-doctor.arrival-type")}
                value={selectedArrivalTypeLabel}
                staticData={arrivalOptions.map((opt) => ({
                  id: opt.value,
                  NAME: opt.label,
                }))}
                displayField="NAME"
                disabled={loadingArrival || arrivalOptions.length === 0}
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    resetAfterArrivalChange();
                    setArrivalType(String(first.id));
                  } else {
                    resetAfterArrivalChange();
                    setArrivalType("");
                  }
                }}
                selectedId={arrivalType || undefined}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>

        <Grid item xs={12} sm={3}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t("patient-request.ask-doctor.department")}
          </Typography>
        </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
              <ByzSelectListPopupInput
                fullWidth
                size="small"
              label={t("patient-request.ask-doctor.department")}
                value={selectedDepartmentLabel}
                staticData={departmentOptions.map((opt) => ({
                  id: opt.value,
                  NAME: opt.label,
                }))}
                displayField="NAME"
                disabled={!arrivalType || loadingDept || departmentOptions.length === 0}
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    resetAfterDepartmentChange();
                    setDepartmentName(String(first.id));
                  } else {
                    resetAfterDepartmentChange();
                    setDepartmentName("");
                  }
                }}
                selectedId={departmentName || undefined}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>

        <Grid item xs={12} sm={3}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t("patient-request.ask-doctor.doctor-role")}
          </Typography>
        </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Box sx={{ width: 320 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.ask-doctor.doctor")}
                  value={selectedDoctorLabel}
                  staticData={doctorOptions.map((opt) => ({
                    id: opt.value,
                    NAME: opt.label,
                  }))}
                  displayField="NAME"
                  disabled={!departmentName || loadingDoctor || doctorOptions.length === 0}
                  onChange={(rows) => {
                    const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                    const first = list[0];
                    if (first) {
                      resetAfterDoctorChange();
                      setDoctorName(String(first.id));
                    } else {
                      resetAfterDoctorChange();
                      setDoctorName("");
                    }
                  }}
                  selectedId={doctorName || undefined}
                  dialogMaxHeight="60vh"
                />
              </Box>
              <Box sx={{ width: 140 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.ask-doctor.role")}
                  value={selectedDoctorRoleLabel}
                  staticData={doctorRoleOptions.map((opt) => ({
                    id: opt.value,
                    NAME: opt.label,
                  }))}
                  displayField="NAME"
                  disabled={!doctorName || loadingDoctorRole || doctorRoleOptions.length === 0}
                  onChange={(rows) => {
                    const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                    const first = list[0];
                    if (first) {
                      setDoctorRole(String(first.id));
                    } else {
                      setDoctorRole("");
                    }
                  }}
                  selectedId={doctorRole || undefined}
                  dialogMaxHeight="60vh"
                />
              </Box>
            </Box>
          </Grid>
        <Grid item xs={12} sm={3}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t("patient-request.ask-doctor.reference-doctor")}
          </Typography>
        </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Box sx={{ width: 320 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.ask-doctor.reference-doctor")}
                  value={selectedReferenceDoctorLabel}
                  staticData={referenceDoctorOptions.map((opt) => ({
                    id: opt.value,
                    NAME: opt.label,
                  }))}
                  displayField="NAME"
                  disabled={
                    !isReferenceDoctor ||
                    !departmentName ||
                    loadingReferenceDoctor ||
                    referenceDoctorOptions.length === 0
                  }
                  onChange={(rows) => {
                    const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                    const first = list[0];
                    if (first) {
                      setReferenceDoctor(String(first.id));
                    } else {
                      setReferenceDoctor("");
                    }
                  }}
                  selectedId={referenceDoctor || undefined}
                  dialogMaxHeight="60vh"
                />
              </Box>
              <ByzMuiCheckBox
                text={t("patient-request.ask-doctor.reference-doctor-check")}
                checked={isReferenceDoctor}
                onChange={(e) => {
                  setIsReferenceDoctor(e.target.checked);
                  if (!e.target.checked) {
                    setReferenceDoctor(""); // Checkbox kapatılınca referans doktor seçimini temizle
                  }
                }}
              />
            </Box>
          </Grid>

        <Grid item xs={12} sm={3}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t("patient-request.ask-doctor.assistant-doctor")}
          </Typography>
        </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.ask-doctor.assistant-doctor")}
                value={selectedAssistantDoctorLabel}
                staticData={assistantDoctorOptions.map((opt) => ({
                  id: opt.value,
                  NAME: opt.label,
                }))}
                displayField="NAME"
                disabled={
                  !departmentName ||
                  loadingAssistantDoctor ||
                  assistantDoctorOptions.length === 0
                }
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    setAssistantDoctor(String(first.id));
                  } else {
                    setAssistantDoctor("");
                  }
                }}
                selectedId={assistantDoctor || undefined}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>

        <Grid item xs={12} sm={3}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t("patient-request.ask-doctor.referring-personnel")}
          </Typography>
        </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.ask-doctor.referring-personnel")}
                value={selectedReferringPersonnelLabel}
                staticData={referringPersonnelOptions.map((opt) => ({
                  id: opt.value,
                  NAME: opt.label,
                }))}
                displayField="NAME"
                disabled={
                  loadingReferringPersonnel || referringPersonnelOptions.length === 0
                }
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    setReferringPersonnel(String(first.id));
                  } else {
                    setReferringPersonnel("");
                  }
                }}
                selectedId={referringPersonnel || undefined}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default AskDoctorModal;

